// src/components/DeliveryActionModal.jsx

import React, { useState } from 'react';
import { FiCheck, FiX, FiTruck, FiRefreshCw } from 'react-icons/fi';

const DeliveryActionModal = ({ isOpen, order, type, onClose, onSubmit, isSubmitting }) => {
    const [description, setDescription] = useState('');

    const isDelivered = type === 'delivered';

    const modalConfig = {
        delivered: {
            title: 'تأیید تحویل سفارش',
            icon: <FiTruck className="text-white text-xl" />,
            bgGradient: 'from-green-600 to-emerald-600',
            buttonText: 'تأیید تحویل',
            buttonIcon: <FiCheck size={20} />,
            descriptionRequired: false,
            placeholder: 'لطفاً نحوه تحویل، رضایت مشتری و هر گونه نکته مهم را ثبت کنید...',
            labelText: 'توضیحات تحویل'
        },
        returned: {
            title: 'برگشت دادن مرسوله',
            icon: <FiRefreshCw className="text-white text-xl" />,
            bgGradient: 'from-red-600 to-rose-600',
            buttonText: 'تأیید برگشت',
            buttonIcon: <FiRefreshCw size={20} />,
            descriptionRequired: true,
            placeholder: 'لطفاً دلیل برگشت مرسوله را وارد کنید...',
            labelText: 'دلیل برگشت'
        }
    };

    const config = modalConfig[type];

    const handleSubmit = () => {
        if (config.descriptionRequired && !description.trim()) {
            alert('لطفاً دلیل برگشت را وارد کنید');
            return;
        }

        const formData = new FormData();
        formData.append('factor_store_id', order.id);
        formData.append('description', description);
        formData.append('type', type);

        onSubmit(formData);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-fadeIn">
                {/* Header با گرادینت متفاوت */}
                <div className={`bg-gradient-to-r ${config.bgGradient} text-white p-5 rounded-t-2xl`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                                {config.icon}
                            </div>
                            <div>
                                <h2 className="text-xl font-bold">{config.title}</h2>
                                <p className="text-white/80 text-sm">شماره سفارش: #{order?.id}</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center hover:bg-white/30 transition-colors"
                        >
                            <FiX className="text-white text-xl" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                    {/* اطلاعات سفارش */}
                    <div className={`rounded-xl p-4 ${isDelivered ? 'bg-green-50' : 'bg-red-50'}`}>
                        <p className="text-sm font-medium text-gray-700 mb-2">اطلاعات سفارش:</p>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-500">شماره سفارش:</span>
                                <span className="font-medium">#{order?.id}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">نام مشتری:</span>
                                <span className="font-medium">{order?.customer_name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">مبلغ فاکتور:</span>
                                <span className="font-medium text-green-600">{new Intl.NumberFormat('fa-IR').format(order?.price)} ریال</span>
                            </div>
                            {order?.remaining_amount > 0 && !isDelivered && (
                                <div className="flex justify-between">
                                    <span className="text-gray-500">مبلغ باقیمانده:</span>
                                    <span className="font-medium text-amber-600">{new Intl.NumberFormat('fa-IR').format(order?.remaining_amount)} ریال</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* فیلد توضیحات */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {config.labelText}
                            {config.descriptionRequired && <span className="text-red-500 mr-1">*</span>}
                            {!config.descriptionRequired && <span className="text-xs text-gray-400 mr-1">(اختیاری)</span>}
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows="4"
                            placeholder={config.placeholder}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            autoFocus
                        />
                        <p className="text-xs text-gray-400 mt-1">
                            {isDelivered
                                ? 'این توضیحات به عنوان سابقه تحویل ثبت خواهد شد.'
                                : 'لطفاً دلیل دقیق برگشت مرسوله را وارد کنید.'}
                        </p>
                    </div>
                </div>

                {/* Buttons */}
                <div className="border-t border-gray-100 p-5 flex gap-3">
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium disabled:opacity-50"
                    >
                        انصراف
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting || (config.descriptionRequired && !description.trim())}
                        className={`flex-1 py-2.5 bg-gradient-to-r ${config.bgGradient} text-white rounded-xl hover:opacity-90 transition-all font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                    >
                        {isSubmitting ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                <span>در حال ثبت...</span>
                            </>
                        ) : (
                            <>
                                {config.buttonIcon}
                                <span>{config.buttonText}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeliveryActionModal;